import { useRef, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

export default function PdfSign() {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(1);
  const [pos, setPos] = useState({ x: 100, y: 100, w: 180, h: 80 });
  const [result, setResult] = useState(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#09090B";
    ctx.lineCap = "round";
  }, []);

  // Pre-load a file from the user's library (?file=<id>) — used after generating a doc.
  useEffect(() => {
    const fileId = searchParams.get("file");
    if (!fileId || file) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
        if (cancelled) return;
        const list = await api.get("/files");
        const meta = list.data.find((f) => f.id === fileId);
        const filename = meta?.original_filename || "document.pdf";
        const blob = new Blob([res.data], { type: "application/pdf" });
        const fileObj = new File([blob], filename, { type: "application/pdf" });
        setFile(fileObj);
        loadThumbs(fileObj);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar el archivo");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function getPos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const cx = (t ? t.clientX : e.clientX) - rect.left;
    const cy = (t ? t.clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  }
  function start(e) {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function move(e) {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function end() {
    drawing.current = false;
  }
  function clearCanvas() {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
  }

  async function loadThumbs(f) {
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/pdf/thumbnails", fd);
      setPages(data.pages);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function onApply() {
    if (!file) return;
    const c = canvasRef.current;
    const blob = await new Promise((resolve) => c.toBlob(resolve, "image/png"));
    const fd = new FormData();
    fd.append("pdf", file);
    fd.append("signature", blob, "signature.png");
    fd.append("page", page);
    fd.append("x", pos.x);
    fd.append("y", pos.y);
    fd.append("width", pos.w);
    fd.append("height", pos.h);
    try {
      const { data } = await api.post("/pdf/sign", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-sign-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_sign")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_sign_desc")}</p>

      <div className="mt-8">
        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => { setFile(fs[0]); loadThumbs(fs[0]); }} testId="sign-dropzone" />
        ) : (
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <span className="eyebrow">{t("pdf_signature_label")}</span>
              <canvas
                ref={canvasRef}
                width={600}
                height={220}
                className="sig-canvas mt-2 w-full"
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
                data-testid="sign-canvas"
              />
              <div className="mt-3 flex gap-3">
                <Button variant="ghost" className="rounded-sm" onClick={clearCanvas} data-testid="sign-canvas-clear">
                  {t("cta_clear")}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                <div>
                  <Label className="eyebrow">{t("pdf_place_on_page")}</Label>
                  <Select value={String(page)} onValueChange={(v) => setPage(parseInt(v))}>
                    <SelectTrigger className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="sign-page-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pages.map((p) => (
                        <SelectItem key={p.page} value={String(p.page)}>Page {p.page}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="eyebrow">{t("pdf_position_x")} (pt)</Label>
                  <Input type="number" value={pos.x} onChange={(e) => setPos({ ...pos, x: parseFloat(e.target.value) })}
                    className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="sign-x" />
                </div>
                <div>
                  <Label className="eyebrow">{t("pdf_position_y")} (pt)</Label>
                  <Input type="number" value={pos.y} onChange={(e) => setPos({ ...pos, y: parseFloat(e.target.value) })}
                    className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="sign-y" />
                </div>
                <div>
                  <Label className="eyebrow">{t("pdf_width")} (pt)</Label>
                  <Input type="number" value={pos.w} onChange={(e) => setPos({ ...pos, w: parseFloat(e.target.value) })}
                    className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="sign-w" />
                </div>
                <div>
                  <Label className="eyebrow">{t("pdf_height")} (pt)</Label>
                  <Input type="number" value={pos.h} onChange={(e) => setPos({ ...pos, h: parseFloat(e.target.value) })}
                    className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="sign-h" />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button onClick={onApply} className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="sign-apply">
                  {t("pdf_sign_apply")} →
                </Button>
                <Button variant="ghost" className="rounded-sm h-11" onClick={() => { setFile(null); setPages([]); setResult(null); }} data-testid="sign-clear-file">
                  {t("cta_clear")}
                </Button>
              </div>
            </div>

            <div>
              <span className="eyebrow">{file.name}</span>
              <div className="mt-2 grid grid-cols-3 gap-3 max-h-[600px] overflow-auto pr-2 border border-zinc-200 rounded-md p-3 bg-zinc-50">
                {pages.map((p) => (
                  <button
                    key={p.page}
                    onClick={() => setPage(p.page)}
                    className={`page-tile ${page === p.page ? "selected" : ""} p-2 rounded-md flex flex-col items-center`}
                    data-testid={`sign-page-${p.page}`}
                  >
                    <img src={p.data_url} alt={`Page ${p.page}`} className="w-full h-auto" />
                    <span className="mt-1 eyebrow">P{p.page}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="sign-result">
            <span className="eyebrow">{t("success")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <div className="mt-4">
              <Button onClick={() => downloadFile(result.id, result.original_filename)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10" data-testid="sign-download">
                ↓ {t("cta_download")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
